import {
  ActivitySpinnerSize,
  ActivitySpinnerWidget,
  ButtonType,
  ButtonWidget,
  CheckBoxWidget,
  DialogFooterButtonsWidget,
  DialogPositioningBehavior,
  DialogType,
  DialogWidget,
  InlineAlertType,
  InlineAlertWidget,
  ProgressBarWidget,
} from '@tableau/widgets';
import React from 'react';

import { CreateCustomDialogFn } from '~commonInfra/dialogs/ReactModalService';
import { Location } from '~commonInfra/utils/Location';
import { Tableau } from '~commonInfra/utils/TableauLog';
import { Messages } from '~src/codegen/Localize';

import { DOWNLOAD_HOLD_MS } from '../details/ContentRelayPlanExecutionsTab';
import { IPlanContentRow } from '../details/planContentsService';
import { typeLabel } from '../details/planContentTypeLabel';
import { executionBundleUrl, executionRowState } from '../details/planExecutionsService';
import { IExecution } from '../serverApi/types/IExecution';
import { IContentRelayPlanDetail } from '../serverApi/types/ISelectionQuery';
import { relayWireContentType } from '../utils/relaySelection';
import { isWireTypeSupported } from '../utils/supportedContentTypes';
import { RunPlanDialogStyles as Styles } from './RunPlanDialogStyles';
import {
  clearActiveRun, getActiveRun, getExecutionStatus, IExecutionPoller, IRunPlanContents, isRunnable, isTerminalState, loadAllPlanContents,
  pollExecution, setActiveRun, triggerExtraction,
} from './runPlanService';

export const RunPlanDialogTestIds = {
  Dialog: 'relay-plan-run-dialog',
  Item: 'relay-plan-run-item-',
  SelectAll: 'relay-plan-run-select-all',
  Summary: 'relay-plan-run-summary',
  FallbackNotice: 'relay-plan-run-fallback',
  TruncatedNotice: 'relay-plan-run-truncated',
  LoadError: 'relay-plan-run-load-error',
  StartError: 'relay-plan-run-start-error',
  Progress: 'relay-plan-run-progress',
  PackageReady: 'relay-plan-run-package-ready',
  Download: 'relay-plan-run-download',
  Cancel: 'relay-plan-run-cancel',
  Confirm: 'relay-plan-run-confirm',
  Close: 'relay-plan-run-close',
};

export interface RunPlanDialogProps {
  plan: IContentRelayPlanDetail;
  onClose: () => void;
}

type Phase = 'loading' | 'loadError' | 'select' | 'starting' | 'running';

function statusLabel(execution: IExecution): string {
  switch (execution.state) {
    case 'RUNNING': return Messages.relayPlanRunRunning();
    case 'SUCCEEDED': return Messages.relayPlanRunSucceeded();
    case 'FAILED': return Messages.relayPlanRunFailed();
    case 'CANCELLED': return Messages.relayPlanRunCancelled();
    default: return Messages.relayPlanRunQueued();
  }
}

function progressKind(execution: IExecution): 'default' | 'error' | 'success' {
  switch (execution.state) {
    case 'SUCCEEDED': return 'success';
    case 'FAILED':
    case 'CANCELLED': return 'error';
    default: return 'default';
  }
}

// Indeterminate until the service reports a percentage (the no-op pipeline never does).
function progressValue(execution: IExecution): number {
  if (execution.state === 'SUCCEEDED') {
    return 100;
  }
  return execution.state === 'RUNNING' && execution.progress > 0 ? execution.progress : undefined;
}

// The service's error text isn't localized or meant for users, so the dialog shows its own and logs this.
function logIfFailed(execution: IExecution): void {
  if (execution.state === 'FAILED') {
    Tableau.Logger().info('Content Relay: run ' + execution.backgroundJobId + ' failed: '
      + execution.errorCode + ' ' + execution.errorMessage);
  }
}

/**
 * The "Run Now" dialog for a Content Relay plan: lists the plan's resolved contents with every runnable
 * item selected, queues an extraction of the selection (the whole plan when everything is selected),
 * then shows the run's status. The run continues server-side when the dialog closes, so Close is always
 * available once it has started.
 */
export function RunPlanDialog(props: RunPlanDialogProps): JSX.Element {
  const plan = props.plan;
  const [phase, setPhase] = React.useState<Phase>('loading');
  const [rows, setRows] = React.useState<ReadonlyArray<IPlanContentRow>>([]);
  const [contents, setContents] = React.useState<IRunPlanContents>(undefined);
  const [selectedKeys, setSelectedKeys] = React.useState<{ [key: string]: boolean }>({});
  const [startError, setStartError] = React.useState(false);
  const [execution, setExecution] = React.useState<IExecution>(undefined);
  const [statusLost, setStatusLost] = React.useState(false);
  const [downloadHeld, setDownloadHeld] = React.useState(false);
  const pollerRef = React.useRef<IExecutionPoller>(undefined);
  const mountedRef = React.useRef(true);

  // Shows a queued run and polls it until it finishes, when it's no longer the plan's active run.
  const followRun = function(queued: IExecution): void {
    setExecution(queued);
    setPhase('running');
    logIfFailed(queued);
    if (isTerminalState(queued.state)) {
      clearActiveRun(plan.luid);
      return;
    }
    pollerRef.current = pollExecution(
      queued.backgroundJobId,
      function(update) {
        setExecution(update);
        logIfFailed(update);
        if (isTerminalState(update.state)) {
          clearActiveRun(plan.luid);
        }
      },
      function() {
        // Its status is unknown, so let the user start another run rather than block them.
        clearActiveRun(plan.luid);
        setStatusLost(true);
      }
    );
  };

  React.useEffect(function() {
    mountedRef.current = true;
    return function() {
      mountedRef.current = false;
      if (pollerRef.current) {
        pollerRef.current.stop();
      }
    };
  }, []);

  React.useEffect(function() {
    let cancelled = false;
    const loadContents = function(): void {
      loadAllPlanContents(plan.luid, plan.selectionQueries).then(
        function(loaded) {
          if (cancelled) {
            return;
          }
          const selected: { [key: string]: boolean } = {};
          loaded.rows.filter(isRunnable).forEach(function(row) { selected[row.key] = true; });
          setRows(loaded.rows);
          setContents(loaded);
          setSelectedKeys(selected);
          setPhase('select');
        },
        function() {
          if (!cancelled) {
            setPhase('loadError');
          }
        }
      );
    };
    // The run's current state, or a new selection when it has finished since the dialog last saw it.
    const resume = function(current: IExecution): void {
      if (cancelled) {
        return;
      }
      if (isTerminalState(current.state)) {
        clearActiveRun(plan.luid);
        loadContents();
      } else {
        followRun(current);
      }
    };
    const activeRun = getActiveRun(plan.luid);
    if (activeRun) {
      // A run started from this dialog earlier may still be going: show it rather than offer a duplicate.
      // Its status is checked first, since it may have finished while the dialog was closed.
      activeRun.then(
        function(queued) {
          if (cancelled) {
            return;
          }
          if (isTerminalState(queued.state)) {
            resume(queued);
            return;
          }
          getExecutionStatus(queued.backgroundJobId).then(resume, function() { resume(queued); });
        },
        function() {
          if (!cancelled) {
            loadContents();
          }
        }
      );
    } else {
      loadContents();
    }
    return function() {
      cancelled = true;
    };
    // followRun only reads plan.luid, which is already a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan.luid, plan.selectionQueries]);

  const isFallback = !!contents && contents.isFallback;
  const runnableRows = rows.filter(isRunnable);
  // No row can be picked (e.g. none carries a LUID) though some can be relayed: the whole plan still can.
  const noneSelectable = !isFallback && !runnableRows.length
    && rows.some(function(row) { return !row.unavailable && row.downloadable; });
  // Fallback rows carry no LUIDs, and a truncated list isn't every item, so these run the whole plan.
  const wholePlanOnly = isFallback || noneSelectable || (!!contents && contents.isTruncated);
  const selectedRows = runnableRows.filter(function(row) { return selectedKeys[row.key]; });
  const canRun = phase === 'select' && (wholePlanOnly ? rows.length > 0 : selectedRows.length > 0);
  const allSelected = runnableRows.length > 0 && selectedRows.length === runnableRows.length;

  const toggleRow = function(key: string): void {
    const next = { ...selectedKeys };
    next[key] = !next[key];
    setSelectedKeys(next);
  };
  const toggleAll = function(): void {
    const next: { [key: string]: boolean } = {};
    if (!allSelected) {
      runnableRows.forEach(function(row) { next[row.key] = true; });
    }
    setSelectedKeys(next);
  };

  const onRun = function(): void {
    if (!canRun) {
      return;
    }
    setPhase('starting');
    setStartError(false);
    // Everything selected runs the whole plan, so related content the server adds is included too.
    const run = triggerExtraction(plan.luid, wholePlanOnly || allSelected ? undefined : selectedRows);
    // Recorded before it's queued, so closing the dialog now still leaves the run to reopen.
    setActiveRun(plan.luid, run);
    run.then(
      function(queued) {
        if (mountedRef.current) {
          followRun(queued);
        }
      },
      function() {
        if (mountedRef.current) {
          setStartError(true);
          setPhase('select');
        }
      }
    );
  };

  const renderItem = function(row: IPlanContentRow): JSX.Element {
    const runnable = isRunnable(row);
    // Rows have no checkboxes when only the whole plan can run, so the not-selectable reasons are for selectable lists only.
    const detail = row.unavailable
      ? Messages.relayPlanContentsUnavailable()
      : !row.downloadable
        ? Messages.relayPlanContentsNotDownloadable()
        : !row.luid && !wholePlanOnly
          ? Messages.relayPlanRunItemNotSelectable()
          : !isWireTypeSupported(relayWireContentType(row.contentType)) && !wholePlanOnly
            ? Messages.relayPlanRunItemTypeNotSupported()
            : row.location ? Messages.relayPlanRunItemType({ TYPE: typeLabel(row.contentType), LOCATION: row.location }) : typeLabel(row.contentType);
    return (
      <div key={row.key} className={Styles.ItemRow} data-tb-test-id={RunPlanDialogTestIds.Item + row.key}>
        {!wholePlanOnly && (
          <CheckBoxWidget
            checked={runnable && !!selectedKeys[row.key]}
            disabled={!runnable || phase !== 'select'}
            handleChange={function() { toggleRow(row.key); }}
            removeLeftMargin={true}
            aria-label={Messages.relayPlanContentsSelectItem({ NAME: row.name || row.id })}
            testId={RunPlanDialogTestIds.Item + row.key + '-select'}
          />
        )}
        <div className={Styles.ItemText}>
          <div className={Styles.ItemName}>{row.name || row.id}</div>
          <div className={Styles.ItemDetail}>{detail}</div>
        </div>
      </div>
    );
  };

  const renderSelect = function(): JSX.Element {
    if (!rows.length) {
      return <div className={Styles.Intro}>{Messages.relayPlanRunEmpty()}</div>;
    }
    return (
      <div>
        <div className={Styles.Intro}>{Messages.relayPlanRunIntro()}</div>
        {startError && (
          <InlineAlertWidget type={InlineAlertType.Error} className={Styles.Alert} testId={RunPlanDialogTestIds.StartError}>
            {Messages.relayPlanRunStartError()}
          </InlineAlertWidget>
        )}
        {(isFallback || noneSelectable) && (
          <InlineAlertWidget type={InlineAlertType.Info} className={Styles.Alert} testId={RunPlanDialogTestIds.FallbackNotice}>
            {Messages.relayPlanRunFallbackNotice()}
          </InlineAlertWidget>
        )}
        {!isFallback && contents.isTruncated && (
          <InlineAlertWidget type={InlineAlertType.Info} className={Styles.Alert} testId={RunPlanDialogTestIds.TruncatedNotice}>
            {Messages.relayPlanRunTruncatedNotice({ SHOWN: String(rows.length), TOTAL: String(contents.totalCount) })}
          </InlineAlertWidget>
        )}
        {!wholePlanOnly && (
          <div className={Styles.ItemRow}>
            <CheckBoxWidget
              checked={allSelected}
              disabled={!runnableRows.length || phase !== 'select'}
              handleChange={toggleAll}
              removeLeftMargin={true}
              aria-label={Messages.relayPlanContentsSelectAll()}
              testId={RunPlanDialogTestIds.SelectAll}
            />
          </div>
        )}
        <div className={Styles.ItemList}>{rows.map(renderItem)}</div>
        {!wholePlanOnly && (
          <div className={Styles.Summary} data-tb-test-id={RunPlanDialogTestIds.Summary}>
            {Messages.relayPlanRunSelectedCount({ SELECTED: String(selectedRows.length), TOTAL: runnableRows.length })}
          </div>
        )}
      </div>
    );
  };

  // Same hold as a Run History row: the download is a browser navigation, so the page can't tell when it ends.
  const onDownload = function(): void {
    if (downloadHeld) {
      return;
    }
    setDownloadHeld(true);
    Location.instance.setUrl(executionBundleUrl(execution));
    window.setTimeout(function() {
      if (mountedRef.current) {
        setDownloadHeld(false);
      }
    }, DOWNLOAD_HOLD_MS);
  };

  const renderPackageReady = function(): JSX.Element {
    return (
      <div className={Styles.PackageReady} data-tb-test-id={RunPlanDialogTestIds.PackageReady}>
        <div className={Styles.PackageReadyTitle}>{Messages.relayPlanRunPackageReadyTitle()}</div>
        <div className={Styles.PackageReadyBox}>
          <span>{Messages.relayPlanRunPackageReadyMessage()}</span>
          <ButtonWidget
            buttonType={ButtonType.Primary}
            onClick={onDownload}
            disabled={downloadHeld}
            testId={RunPlanDialogTestIds.Download}
          >
            {downloadHeld ? Messages.relayPlanExecutionsDownloadPreparing() : Messages.relayPlanExecutionsDownload()}
          </ButtonWidget>
        </div>
      </div>
    );
  };

  const renderRunning = function(): JSX.Element {
    const failed = execution.state === 'FAILED';
    const message = statusLost
      ? Messages.relayPlanRunStatusUnknown()
      : failed
        ? Messages.relayPlanRunFailedMessage()
        : isTerminalState(execution.state) ? undefined : Messages.relayPlanRunBackgroundNote();
    if (execution.state === 'SUCCEEDED' && !statusLost && !!executionBundleUrl(execution) && executionRowState(execution) === 'ready') {
      return renderPackageReady();
    }
    return (
      <ProgressBarWidget
        className={Styles.ProgressBar}
        label={statusLabel(execution)}
        kind={progressKind(execution)}
        value={progressValue(execution)}
        max={100}
        message={message}
        testId={RunPlanDialogTestIds.Progress}
      />
    );
  };

  let body: JSX.Element;
  if (phase === 'loading') {
    body = <div className={Styles.Centered}><ActivitySpinnerWidget spinnerSize={ActivitySpinnerSize.Large} /></div>;
  } else if (phase === 'loadError') {
    body = (
      <InlineAlertWidget type={InlineAlertType.Error} testId={RunPlanDialogTestIds.LoadError}>
        {Messages.relayPlanRunLoadError()}
      </InlineAlertWidget>
    );
  } else if (phase === 'running') {
    body = renderRunning();
  } else {
    body = renderSelect();
  }

  const footer = phase === 'running'
    ? (
      <DialogFooterButtonsWidget
        buttons={[
          { buttonType: ButtonType.Primary, onClick: props.onClose, children: Messages.close(), testId: RunPlanDialogTestIds.Close },
        ]}
      />
    )
    : (
      <DialogFooterButtonsWidget
        buttons={[
          {
            buttonType: ButtonType.Outline,
            onClick: props.onClose,
            children: Messages.relayPlanDialogCancel(),
            testId: RunPlanDialogTestIds.Cancel,
          },
          {
            buttonType: ButtonType.Primary,
            onClick: onRun,
            disabled: !canRun,
            children: Messages.runNow(),
            testId: RunPlanDialogTestIds.Confirm,
          },
        ]}
      />
    );

  return (
    <DialogWidget
      type={DialogType.NormalWithTitleBar}
      title={Messages.relayPlanRunTitle()}
      showCloseButton={true}
      onCloseRequest={props.onClose}
      // Draggable, and recentered as the item list loads in (until the user drags it).
      positioningBehavior={DialogPositioningBehavior.Draggable}
      shouldRecenterOnUpdate={true}
      isModal={true}
      dialogWidth={560}
      footerContent={footer}
      testId={RunPlanDialogTestIds.Dialog}
    >
      {body}
    </DialogWidget>
  );
}

/** Options for ReactModalService.openCustom. */
export function RunPlanDialogOpts(plan: IContentRelayPlanDetail, onClosed?: () => void): CreateCustomDialogFn {
  return function(closeAndDismiss) {
    const onClose = function(): void {
      closeAndDismiss.close();
      if (onClosed) {
        onClosed();
      }
    };
    return <RunPlanDialog plan={plan} onClose={onClose} />;
  };
}
